import { useAction, useAtom } from '@reatom/npm-react'
import { addMonths, isSameMonth, startOfMonth, subMonths } from 'date-fns'
import styled, { css } from 'styled-components'

import { Array, AsyncStateN } from '@blade/std-lib'
import { Button, Numeric, Tooltip } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { Trend } from '@root/entities/trend'
import { UserTradingAnalyticsPriceType } from '@root/features/user/user-trading-analytics/models/user-trading-analytics-price-type'
import {
  CURRENCY_AMOUNT_NATIVE_PRESET,
  PnlUsdFormatted,
} from '@root/shared/core/format'
import { useDateFormat, useTranslation } from '@root/shared/localization'
import {
  ChartIcon,
  ChevronLeftExactIcon,
  ChevronRightExactIcon,
  InfoMessage,
  mediaMaxMobile,
} from '@root/shared/ui-kit'

import { PnlCalendarDaily } from '../../model'

import { PnlCalendarEmpty } from './pnl-calendar-empty'
import { PnlCalendarLoading } from './pnl-calendar-loading'
import { PnlCalendarTooltipContent } from './pnl-calendar-tooltip-content'

import type { UserPnlCalendarModel } from '../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = ClassNameProps & {
  model: UserPnlCalendarModel
  priceType: UserTradingAnalyticsPriceType
  onToggleView: () => void
}

export const PnlCalendar: React.FC<Props> = ({
  className,
  model,
  priceType,
  onToggleView,
}) => {
  const { t } = useTranslation()

  const { formatDate } = useDateFormat()
  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)

  const [dataState] = useAtom(model)
  const retry = useAction(model.retryAction)
  const [, setDate] = useAtom(model.date)
  const [visibleMonth] = useAtom(model.visibleMonth)
  const [dailyByDay] = useAtom(model.dailyByDay)
  const [_canGoPrev] = useAtom(model.canGoPrev)
  const [canGoNext] = useAtom(model.canGoNext)
  const [gridDays] = useAtom(model.gridDays)

  const monthTitle = formatDate(visibleMonth, 'LLLL yyyy')

  const handleMonthNavigation = (direction: 'prev' | 'next') => {
    const next =
      direction === 'prev'
        ? subMonths(visibleMonth, 1)
        : addMonths(visibleMonth, 1)
    setDate(startOfMonth(next))
  }

  return (
    <Root className={className}>
      <Header>
        <ViewToggle>
          <ViewToggleButton onClick={onToggleView} type="button">
            <ChartIcon size={16} />
            {t('portfolio.pnl-chart.chart-view')}
          </ViewToggleButton>
        </ViewToggle>
        <Nav>
          <NavButton
            type="button"
            // disabled={!canGoPrev}
            onClick={() => handleMonthNavigation('prev')}
            iconLeft={<ChevronLeftExactIcon size={10} />}
            variant="ghost"
            size="xxs"
            radius="rounded"
          />
          <MonthTitle>{monthTitle}</MonthTitle>
          <NavButton
            type="button"
            disabled={!canGoNext}
            onClick={() => handleMonthNavigation('next')}
            iconRight={<ChevronRightExactIcon size={10} />}
            variant="ghost"
            size="xxs"
            radius="rounded"
          />
        </Nav>
      </Header>

      {AsyncStateN.matchWaiting(dataState, {
        Success: (data) => {
          if (!Array.isNotEmpty(data)) {
            return <PnlCalendarEmpty />
          }

          return (
            <DaysGrid>
              {gridDays.map((day) => {
                const inMonth = isSameMonth(day, visibleMonth)
                const dayKey = PnlCalendarDaily.toDayKey(day)
                const pnlEntry = dailyByDay.get(dayKey)

                const trend = pnlEntry
                  ? Trend.of(pnlEntry.pnlNative)
                  : Trend.Neutral

                return (
                  <Tooltip
                    content={
                      <PnlCalendarTooltipContent
                        pnlUsd={pnlEntry?.pnlUsd}
                        date={day}
                      />
                    }
                    key={day.toISOString()}
                  >
                    <DayCell
                      key={day.toISOString()}
                      aria-hidden={!inMonth}
                      data-trend={trend}
                    >
                      {pnlEntry ? (
                        priceType === UserTradingAnalyticsPriceType.USD ? (
                          <PnlUsdFormatted pnl={pnlEntry.pnlUsd} />
                        ) : (
                          <Numeric
                            forceSign
                            value={pnlEntry.pnlNative}
                            preset={CURRENCY_AMOUNT_NATIVE_PRESET}
                            currency={nativeCurrency.ticker}
                          />
                        )
                      ) : (
                        <>&nbsp;</>
                      )}
                    </DayCell>
                  </Tooltip>
                )
              })}
            </DaysGrid>
          )
        },
        Error: () => (
          <InfoMessage
            onRetry={() => retry()}
            intent="error"
            variant="dt"
            withBackground={false}
          />
        ),
        Waiting: () => <PnlCalendarLoading />,
      })}
    </Root>
  )
}

const Root = styled.div`
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 20px;
  height: 100%;
  min-height: 0;

  padding-inline: 16px;
  padding-block-end: 16px;

  ${mediaMaxMobile(css`
    padding-inline: 14px;
    padding-block-end: 14px;
    padding-top: 10px;
    gap: 14px;
  `)}
`

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 12px;
  margin-top: 16px;

  ${mediaMaxMobile(css`
    margin-top: 14px;
  `)}
`

const ViewToggle = styled.div`
  display: grid;
  justify-content: start;
  margin-bottom: 4px;
`

const ViewToggleButton = styled.button`
  all: unset;
  cursor: pointer;
  color: var(--textA-9);

  display: grid;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;

  grid-template-columns: auto auto;
  gap: 6px;
  align-content: center;
  align-items: center;
`

const MonthTitle = styled.div`
  color: var(--text-11);

  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
`

const NavButton = styled(Button)`
  color: var(--text-9);

  &:disabled {
    opacity: 0.4;
  }
`

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  grid-template-rows: repeat(5, minmax(0, 1fr));
  height: 100%;
  min-height: 0;
  gap: 6px;
`

const DayCell = styled.div`
  background: var(--backgroundA-2);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px;
  min-width: 0;
  border-radius: 4px;

  text-align: center;

  font-size: 11px;
  font-weight: 600;

  &[data-trend='up'] {
    color: var(--up-9);
    background: var(--upA-2);
  }
  &[data-trend='down'] {
    color: var(--down-9);
    background: var(--downA-2);
  }
  &[data-trend='neutral'] {
    color: var(--text-11);
    background: var(--backgroundA-2);
  }
  &[aria-hidden='true'] {
    opacity: 0;
  }
`

const Nav = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  gap: 20px;

  align-items: center;
`
